import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';

import { DTOHashtagInfo } from 'src/apis/dto/DTOHashtagInfo';
import { DTOProfileCard, DTOProfileCardType } from 'src/apis/dto/DTOProfileCard';
import { DTOProfileImageInfo } from 'src/apis/dto/DTOProfileImageInfo';
import { DTOProfileInterestResponse } from 'src/apis/dto/DTOProfileInterestResponse';
import { DTORecommendedHashtagInfo } from 'src/apis/dto/DTORecommendedHashtagInfo';
import useLogin from 'src/hooks/useLogin';
import { RestQueryOptions, useRestQuery } from 'src/hooks/useRestQuery';
import {
  deleteProfileImageAPI,
  getProfileAPI,
  getProfileCardAPI,
  orderProfileImagesAPI,
  updateHashtagsAPI,
  updateMainProfileImageAPI,
  updateProfileBioAPI,
  uploadProfileImageAPI,
} from 'src/stores/profile/apis';
import { uploadContentManager } from 'src/utils/content-manager';
import { isNotNull } from 'src/utils/object';
import { optimisticUpdaterFactory } from 'src/utils/query';

export enum ProfileApiList {
  RecommendedHashtagList = '/api/v1/hashtags',
  GetProfileInterest = '/api/v1/interests/profile',
}

export const ProfileQueryKeys = {
  getProfileCard: (type?: DTOProfileCardType, id?: string) =>
    ['getProfileCard', type, id].filter<string>(isNotNull),
};

export type UseGetProfileCardQueryParams =
  | {
      type: Exclude<DTOProfileCardType, 'MY_PROFILE'>;
      id: string;
    }
  | {
      type: Extract<DTOProfileCardType, 'MY_PROFILE'>;
      id?: undefined;
    };

export const useGetProfileCardQuery = (
  { type, id }: UseGetProfileCardQueryParams,
  options?: Omit<
    UseQueryOptions<DTOProfileCard, unknown, DTOProfileCard, string[]>,
    'queryKey' | 'queryFn'
  >
) => {
  const { userId, isLogin } = useLogin();

  if (type !== 'MY_PROFILE' && id == null) {
    throw new Error("id is required to see other user's profile");
  }

  return useQuery({
    queryKey: ProfileQueryKeys.getProfileCard(type, id),
    async queryFn() {
      if (!isLogin) {
        throw new Error('Only logged in users can view profiles.');
      }

      if (type === 'MY_PROFILE') {
        const response = await getProfileAPI(userId);

        return response.data;
      }

      return getProfileCardAPI(type, id);
    },
    enabled: isLogin,
    ...options,
  });
};

export const useGetRecommendedHashtagsQuery = () => {
  return useRestQuery<void, DTORecommendedHashtagInfo[], unknown>({
    path: ProfileApiList.RecommendedHashtagList,
    options: {
      gcTime: Infinity,
      staleTime: Infinity,
    },
  });
};

export const useUpdateProfileMutation = (
  options?: UseMutationOptions<[string, DTOHashtagInfo[]], unknown, [string, string[]], void>
) => {
  const queryClient = useQueryClient();

  return useMutation<[string, DTOHashtagInfo[]], unknown, [string, string[]], void>({
    async mutationFn([profileBio, hashtags]) {
      const [{ data: newProfileBio }, { data: newHashtagInfos }] = await Promise.all([
        updateProfileBioAPI({ profileBio }),
        updateHashtagsAPI({
          hashtags,
        }),
      ]);

      return [newProfileBio.result, newHashtagInfos];
    },
    ...options,
    async onSuccess([profileBio, hashtagInfos], ...args) {
      queryClient.setQueryData(
        ProfileQueryKeys.getProfileCard('MY_PROFILE'),
        optimisticUpdaterFactory<DTOProfileCard>((prev) => {
          prev.profileBio = profileBio;
          prev.hashtagInfos = hashtagInfos;
        })
      );

      await options?.onSuccess?.([profileBio, hashtagInfos], ...args);
    },
  });
};

export const useUploadProfileImageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(file: File) {
      const fileInfo = await uploadContentManager(file);
      const { data } = await uploadProfileImageAPI(fileInfo);
      return data;
    },
    onSuccess(data) {
      queryClient.setQueryData(
        ProfileQueryKeys.getProfileCard('MY_PROFILE'),
        optimisticUpdaterFactory<DTOProfileCard>((prev) => {
          prev.profileImageInfos = data;
        })
      );
    },
  });
};

export const useOrderProfileImagesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(profileImageInfos: DTOProfileCard['profileImageInfos']) {
      const orders = profileImageInfos.map(({ id }) => id);
      const { data } = await orderProfileImagesAPI({ orders });
      return data;
    },
    onSuccess(data) {
      queryClient.setQueryData(
        ProfileQueryKeys.getProfileCard('MY_PROFILE'),
        optimisticUpdaterFactory<DTOProfileCard>((prev) => {
          prev.profileImageInfos = data;
        })
      );
    },
  });
};

export const useUpdateMainProfileImageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(file: File) {
      const fileInfo = await uploadContentManager(file);
      const { data } = await updateMainProfileImageAPI(fileInfo);
      return data;
    },
    onSuccess(data) {
      queryClient.setQueryData(
        ProfileQueryKeys.getProfileCard('MY_PROFILE'),
        optimisticUpdaterFactory<DTOProfileCard>((prev) => {
          prev.profileImageInfos = data;
        })
      );
    },
  });
};

export const useDeleteProfileImageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(profileImageId: DTOProfileImageInfo['id']) {
      const response = await deleteProfileImageAPI(profileImageId);
      return response.data;
    },
    onSuccess(data) {
      queryClient.setQueryData(
        ProfileQueryKeys.getProfileCard('MY_PROFILE'),
        optimisticUpdaterFactory<DTOProfileCard>((prev) => {
          prev.profileImageInfos = data;
        })
      );
    },
  });
};

export const useProfileInterestQuery = (
  options: RestQueryOptions<DTOProfileInterestResponse, void> = {}
) => {
  return useRestQuery<void, DTOProfileInterestResponse, void>({
    path: ProfileApiList.GetProfileInterest,
    options,
  });
};
